import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Col, Row, Spinner } from "reactstrap";
import { AutoSizer, Table, Column } from "react-virtualized";
import useDashboard from "../useDashboard";
import EmptyDataMessage from "../../../sharedComponents/ErrorMessages/EmptyDataMessage";
import Title from "../../../sharedComponents/Title/Title";
import formatters from "../../../config/formatters";
import { tableColors } from "../../../config/constants";  
import { Chart as ChartJS, registerables } from "chart.js";

ChartJS.register(...registerables);

const SegmentOverviewCard = () => {
    const { numberFormatter, percentFormatter } = formatters();
    const segmentOverview = useSelector(({dashboard}) => dashboard.segmentOverview);
    const isLoading = useSelector(({dashboard}) => dashboard.segmentOverviewIsLoading);
    const { getSegmentOverview } = useDashboard.useDates();

    const formatSegmentName = (value) => {
        if (!value) return "";
        return value.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    };

    useEffect(() => {
        getSegmentOverview();
    }, []);

    useEffect(() => {
        if (isLoading || !segmentOverview?.length) return;
        const labels = segmentOverview.map((s) => formatSegmentName(s.segment));
        const values = segmentOverview.map((s) => s.total_cases);
        const canvasId = "segmentOverviewCanvas";
        const existing = ChartJS.getChart(canvasId);
        if (existing) existing.destroy();
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        new ChartJS(canvas, {
            type: "doughnut",
            data: {
                labels,
                datasets: [
                    {
                        data: values,
                        backgroundColor: tableColors
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true }
                }
            }
        });
    }, [segmentOverview, isLoading]);

    return (
        <Col className="p-0 d-flex flex-column" style={{ height: "100%" }}>
            <Title title="Segment Overview" />
            {isLoading && (
                <Row
                    className="m-0 p-0 d-flex justify-content-center align-items-center"
                    style={{ height: "300px" }}
                >
                    <Spinner color="beplBlue" />
                </Row>
            )}
            {!isLoading && segmentOverview?.length === 0 && (
                <Row style={{ height: "300px" }}>
                    <EmptyDataMessage />
                </Row>
            )}
            {!isLoading && segmentOverview?.length > 0 && (
                <Row className="mx-0 my-2 flex-grow-1" style={{ minHeight: 0, height: "100%" }}>
                    <Col
                        className="d-flex justify-content-center align-items-center p-0"
                        style={{ flex: "0 0 42%", height: "100%" }}
                    >
                        <div style={{ width: "90%", height: "90%" }}>
                            <canvas
                                id="segmentOverviewCanvas"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    </Col>
                    <Col
                        className="p-0 text-size-14 d-flex flex-column"
                        style={{ flex: "0 0 58%", height: "100%", minHeight: 0 }}
                    >
                        <div className="flex-grow-1 d-flex" style={{ minHeight: 0, textTransform: "capitalize" }}>
                            <AutoSizer>
                                {({ width, height }) => (
                                    <Table
                                        width={width}
                                        height={height}        
                                        headerHeight={34}
                                        rowHeight={36}
                                        rowCount={segmentOverview?.length}
                                        rowGetter={({ index }) => segmentOverview?.[index]}
                                        className="table segmentOverviewTable border-grayLight"
                                    >
                                        <Column
                                            label="Segment"
                                            dataKey="segment"
                                            width={width * 0.50}
                                            cellRenderer={({ cellData }) => formatSegmentName(cellData)}
                                        />
                                        <Column
                                            label="Cases"
                                            dataKey="total_cases"
                                            width={width * 0.25}
                                            cellRenderer={({ cellData }) => numberFormatter.format(cellData)}
                                        />
                                        <Column
                                            label="% of Sales"
                                            dataKey="sales_percent"
                                            width={width * 0.25}
                                            cellRenderer={({ cellData }) => percentFormatter.format(cellData)}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </div>
                    </Col>
                </Row>
            )}
        </Col>
    );
};

export default SegmentOverviewCard;